import {
  CASE_OUTCOMES,
  type CaseOutcome,
  type ReportBundle,
  type RunDetail,
} from '@/lib/api/quality-types.js';
import {
  caseOutcome,
  isUnconfirmedDefect,
  isUnconfirmedQaAssessment,
  outcomeProjection,
} from '../quality-outcome.js';

export const AGENT_ACTIONS = [
  'file_defects',
  'rework_cases',
  'report_qa_errors',
] as const;

export type AgentAction = (typeof AGENT_ACTIONS)[number];

export interface AgentActionHandoff {
  action: AgentAction;
  caseIds: string[];
  outcomes: CaseOutcome[];
  reportRevision: number;
}

export type AgentActionHandoffResult =
  | { kind: 'none' }
  | { kind: 'invalid'; message: string }
  | { kind: 'valid'; value: AgentActionHandoff };

const MAX_CASES = 50;
const MAX_OUTCOMES = CASE_OUTCOMES.length;
const MAX_CASE_ID_LENGTH = 500;

function isValidStringArray(
  value: unknown,
  maximumItems: number,
  maximumLength?: number,
): value is string[] {
  if (
    !Array.isArray(value) ||
    value.length === 0 ||
    value.length > maximumItems ||
    !value.every(
      (entry) =>
        typeof entry === 'string' &&
        entry.length > 0 &&
        entry.trim().length > 0 &&
        (maximumLength === undefined || entry.length <= maximumLength),
    )
  ) {
    return false;
  }
  return new Set(value).size === value.length;
}

function parseStringArray(
  raw: unknown,
  maximumItems: number,
  maximumLength?: number,
): string[] | null {
  if (typeof raw !== 'string' && !Array.isArray(raw)) return null;
  try {
    const value: unknown = typeof raw === 'string' ? JSON.parse(raw) : raw;
    return isValidStringArray(value, maximumItems, maximumLength)
      ? value
      : null;
  } catch {
    return null;
  }
}

/** Parse an Admin-to-Web handoff without trimming or splitting source case identities. */
export function parseAgentActionHandoff(
  search: Record<string, unknown>,
  href?: string,
): AgentActionHandoffResult {
  if (search.agentAction === undefined) return { kind: 'none' };
  if (href && new TextEncoder().encode(new URL(href).href).length > 7000) {
    return {
      kind: 'invalid',
      message:
        'This selection exceeds the supported 7000-byte Agent link. Select fewer cases.',
    };
  }
  if (
    typeof search.agentAction !== 'string' ||
    !AGENT_ACTIONS.includes(search.agentAction as AgentAction)
  ) {
    return { kind: 'invalid', message: 'This Agent action is not supported.' };
  }
  const caseIds = parseStringArray(
    search.caseIds,
    MAX_CASES,
    MAX_CASE_ID_LENGTH,
  );
  const rawOutcomes = parseStringArray(search.outcomes, MAX_OUTCOMES);
  if (!caseIds || !rawOutcomes) {
    return {
      kind: 'invalid',
      message: 'The selected cases in this Agent action are invalid.',
    };
  }
  if (
    !rawOutcomes.every((outcome) =>
      CASE_OUTCOMES.includes(outcome as CaseOutcome),
    )
  ) {
    return {
      kind: 'invalid',
      message: 'The Agent action contains an unknown case outcome.',
    };
  }
  const allowed =
    search.agentAction === 'file_defects'
      ? ['defect_found']
      : search.agentAction === 'rework_cases'
        ? ['case_unclear']
        : ['qa_error', 'infra_error'];
  if (rawOutcomes.some((outcome) => !allowed.includes(outcome))) {
    return {
      kind: 'invalid',
      message: 'The selected outcomes do not match this Agent action.',
    };
  }
  const rawRevision = search.reportRevision;
  const reportRevision =
    typeof rawRevision === 'number' ||
    (typeof rawRevision === 'string' && /^[1-9]\d*$/.test(rawRevision))
      ? Number(rawRevision)
      : NaN;
  if (!Number.isSafeInteger(reportRevision) || reportRevision < 1) {
    return {
      kind: 'invalid',
      message: 'The Agent action has no valid report revision.',
    };
  }
  return {
    kind: 'valid',
    value: {
      action: search.agentAction as AgentAction,
      caseIds,
      outcomes: rawOutcomes as CaseOutcome[],
      reportRevision,
    },
  };
}

/** Validate a parsed handoff against the freshly fetched canonical detail and coherent report. */
export function validateAgentActionHandoff(
  handoff: AgentActionHandoff,
  detail: RunDetail,
  bundle: ReportBundle | undefined,
): string | null {
  if (!isValidStringArray(handoff.caseIds, MAX_CASES, MAX_CASE_ID_LENGTH)) {
    return 'The selected cases in this Agent action are invalid.';
  }
  if (!isValidStringArray(handoff.outcomes, MAX_OUTCOMES)) {
    return 'The selected outcomes in this Agent action are invalid.';
  }
  const projection = outcomeProjection(detail.run, detail.summary);
  if (projection.kind !== 'canonical')
    return 'This action requires canonical outcome data.';
  if ((detail.run.run_kind ?? 'execution') !== 'execution')
    return 'Business-case Agent actions are available only for execution runs.';
  if (
    projection.reportRevision !== handoff.reportRevision ||
    bundle?.coherence !== 'coherent' ||
    bundle.reportRevision !== handoff.reportRevision
  ) {
    return 'The report changed after this action was selected. Open the current report and select the cases again.';
  }
  const byId = new Map(
    detail.results.map((result) => [result.case_id, result]),
  );
  const selectedOutcomes = new Set(handoff.outcomes);
  const currentOutcomes = new Set<CaseOutcome>();
  for (const caseId of handoff.caseIds) {
    const result = byId.get(caseId);
    const current = result ? caseOutcome(result) : null;
    if (!result || !current || !selectedOutcomes.has(current)) {
      return 'At least one selected case is missing or has a different current outcome.';
    }
    if (isUnconfirmedQaAssessment(result)) {
      return 'At least one selected case is an unconfirmed Digital QA result. Reassess it before reporting it as a QA error.';
    }
    if (isUnconfirmedDefect(result)) {
      return 'At least one selected case requires review. Confirm it as a defect before filing it.';
    }
    currentOutcomes.add(current);
  }
  if (
    currentOutcomes.size !== selectedOutcomes.size ||
    handoff.outcomes.some((outcome) => !currentOutcomes.has(outcome))
  ) {
    return 'The selected outcome groups do not match the current cases.';
  }
  const allowed = new Set<CaseOutcome>(
    handoff.action === 'file_defects'
      ? ['defect_found']
      : handoff.action === 'rework_cases'
        ? ['case_unclear']
        : ['qa_error', 'infra_error'],
  );
  if (handoff.outcomes.some((outcome) => !allowed.has(outcome))) {
    return 'The selected outcomes do not match this Agent action.';
  }
  return null;
}
